import React, { useRef } from "react"
import PropTypes from "prop-types"
import { NavLink, useMatch, Link } from "react-router-dom"
import { oldRoutes } from "router/old-routes"
import { routes } from "router/routes"
import UserMenu from "components/common/UserMenu"
import GuideNavigation from "./GuideNavigation"
import TripFiltersNavigation from "./TripFiltersNavigation"
import SearchTrips from "./SearchTrips"
import withDefaultErrorBoundary from "modules/errors/HOCs/withDefaultErrorBoundary"
import Logo from "components/common/Logo"
import useEvent from "hooks/useEvent"
import { useDispatch, useSelector } from "react-redux"
import { updateWindowSize } from "modules/window/reducer"
import { isGuideSelector, isUserSelector, updateUser } from "store/user"
import { useTheme } from "context/AntdThemeContext"
import { themes } from "constants/theme"
import { useTranslation } from "react-i18next"
import { isProApp } from "utils/app-type"
import { getHostsByEnvironment } from "utils/host-urls"
import { Button, Switch } from "antd"
import styles from "./style.module.css"
import { Container } from "reactstrap"

Header.propTypes = {
  noPadding: PropTypes.bool,
  fluid: PropTypes.bool,
  withNavigation: PropTypes.bool,
  withBotomBorder: PropTypes.bool,
  className: PropTypes.string
}

function Header({ noPadding = false, fluid = false, withNavigation = false, withBotomBorder: bottomBorder = false, className }) {
  const dispatch = useDispatch()
  const headerRef = useRef(null)
  const headerHeight = useSelector((store) => store.window)
  const { switchTheme, isSportsman } = useTheme()
  const isGuide = useSelector(isGuideSelector)
  const isUser = useSelector(isUserSelector)
  const { loggedIn } = useSelector((store) => store.auth)
  const { t } = useTranslation()

  const isProHost = isProApp()
  const { PROTOCOL, PRO_LANDING } = getHostsByEnvironment()
  const isClientLandingPage = !!useMatch(routes.rootPath()) && !isProHost
  const isGuideProfile = !!useMatch(routes.proGuideProfilePath())

  const isTripsPage = !!useMatch(oldRoutes.tripsPath())
  const rootPath = (isGuide && oldRoutes.guideRootPath()) || (isUser && oldRoutes.clientRootPath()) || routes.rootPath()

  const showGuideNavigation = withNavigation && isGuide
  const showTripFiltersNavigation = (isTripsPage && !isGuide) || isClientLandingPage
  const withBotomBorder = bottomBorder || showGuideNavigation || showTripFiltersNavigation
  const setHeight = () => {
    if (!headerRef.current) return
    const height = headerRef.current.clientHeight
    const isHeightChanged = headerHeight !== height
    if (!isHeightChanged) return
    document.body.style.setProperty("--header-height", `${height}px`)
    dispatch(updateWindowSize({ headerHeight: height }))
  }

  useEvent("load", setHeight, true, window, true, true)
  useEvent("resize", setHeight, true, window)

  const classes = ["bg-white py-10 py-sm-15 px-40 position-relative z-1"]
  if (withBotomBorder) classes.push("border-bottom border-gray-lightest")
  if (className) classes.push(className)

  const handleThemeChange = (checked) => {
    switchTheme(checked ? themes.sportsman : themes.sportswoman)
    dispatch(updateUser({ adventure_mode: checked ? themes.sportsman : themes.sportswoman }))
  }

  const scrollToRequestDemo = () => {
    const demoRequestEl = document.querySelector("#demo-request")
    if (demoRequestEl) demoRequestEl.scrollIntoView({ behavior: "smooth" })
  }

  return (
    <div className="sticky-top" ref={headerRef} style={{ ...(isTripsPage && { maxHeight: 140 }) }}>
      <header className={classes.join(" ")}>
        <Container fluid={fluid || isTripsPage}>
          <div className="hstack gap-10 justify-content-between flex-wrap">
            <div className="d-flex gap-50 align-items-center">
              <NavLink
                className="d-block rounded-1 bg-gray-light-hover bg-opacity-25-hover text-dark p-2 m-n2"
                to={rootPath}
                state={{ scrollToTop: true }}
                title="Home"
                size={32}
              >
                {isSportsman ? (
                  <Logo alt="Logo" width={35} height={35} wide />
                ) : (
                  <Logo alt="Logo" width={35} height={35} is_sportswoman wide />
                )}
              </NavLink>
              {!loggedIn && (
                <>
                  {!isProHost && (
                    <a href={PROTOCOL + PRO_LANDING} target="_blank" rel="noopener noreferrer">
                      <Button className={styles.headerBtn} type="primary">
                        {t("global.become_a_guide")}
                      </Button>
                    </a>
                  )}
                  {isProHost && !isGuideProfile && (
                    <Button
                      className="text-dark text-decoration-none opacity-50-hover fw-medium d-none d-md-block"
                      style={{ fontFamily: "Epilogue" }}
                      type="link"
                      onClick={scrollToRequestDemo}
                    >
                      See a Demo
                    </Button>
                  )}
                </>
              )}
            </div>
            <div>{!isProHost && <SearchTrips className="order-2 order-md-1 flex-grow-1 flex-md-grow-0 pr-5xl" />}</div>
            {!loggedIn && (
              <Link to={routes.signInV2()}>
                <Button className={`${styles.headerBtn}  px-m py-s`} type="primary">
                  {t("global.sign_in")}
                </Button>
              </Link>
            )}
            {!loggedIn && <UserMenu className="d-block d-md-none" />}
            {loggedIn && (
              <div className="order-1 ">
                <div className="d-flex align-items-center">
                  <Switch checked={isSportsman} onChange={handleThemeChange} className="m-0 me-10 theme-toggle" />
                  <UserMenu className="order-1" />
                </div>
              </div>
            )}
          </div>
        </Container>
      </header>
      {showGuideNavigation ? <GuideNavigation /> : null}
      {showTripFiltersNavigation ? <TripFiltersNavigation visible={isClientLandingPage} fluid={!isClientLandingPage} /> : null}
    </div>
  )
}

export default withDefaultErrorBoundary(Header)
